import React, { useReducer, useEffect, useRef, useCallback, useMemo } from 'react';
import GameCard from './GameCard.jsx';

const initialState = {
  games: [],
  page: 1,
  loading: true,
  hasMore: true,
  error: null,
};

function gameListReducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        games: state.page === 1 ? action.payload.games : [...state.games, ...action.payload.games],
        hasMore: !action.payload.pagination.is_last_page,
      };
    case 'FETCH_FAILURE':
      return { ...state, loading: false, error: action.payload };
    case 'LOAD_MORE':
      if (state.hasMore && !state.loading) {
        return { ...state, page: state.page + 1 };
      }
      return state;
    case 'RESET_FILTERS':
      return { ...initialState, loading: true };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

const GameList = ({ filters, onCardClick, onEditClick, isAdmin, onTotalCountChange, onCategoryClick, onLoadingChange, showGroups = true }) => {
  const [state, dispatch] = useReducer(gameListReducer, initialState);
  const loaderRef = useRef(null);

  const stableOnTotalCountChange = useCallback(onTotalCountChange, []);
  const stableOnLoadingChange = useCallback(onLoadingChange, []);

  useEffect(() => {
    stableOnLoadingChange(state.loading);
  }, [state.loading, stableOnLoadingChange]);

  useEffect(() => {
    dispatch({ type: 'RESET_FILTERS' });
  }, [filters]);

  useEffect(() => {
    if (!state.hasMore && state.page > 1) {
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    const fetchGames = async () => {
      dispatch({ type: 'FETCH_START' });

      const params = new URLSearchParams();
      params.append('page', state.page);
      if (filters.searchTerm) params.append('search', filters.searchTerm);
      if (filters.playerCount) params.append('players', filters.playerCount);
      if (filters.selectedCategory) params.append('category', filters.selectedCategory);
      if (filters.sortBy) params.append('sort', filters.sortBy);
      if (filters.sortDirection) params.append('direction', filters.sortDirection);

      try {
        const response = await fetch(`/api/v1/games.json?${params.toString()}`, { signal });
        const data = await response.json();

        dispatch({ type: 'FETCH_SUCCESS', payload: data });
        stableOnTotalCountChange(data.pagination.total_count);
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted');
        } else {
          console.error("Error fetching games:", error);
          dispatch({ type: 'FETCH_FAILURE', payload: error.message });
        }
      }
    };

    fetchGames();

    return () => controller.abort();
  }, [state.page, filters, stableOnTotalCountChange]);

  useEffect(() => {
    if (!state.hasMore || state.loading) {
      return;
    }

    const currentLoader = loaderRef.current;
    if (!currentLoader) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !state.loading) {
          dispatch({ type: 'LOAD_MORE' });
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(currentLoader);
    return () => observer.disconnect();
  }, [state.hasMore, state.loading]);

  const processedGames = state.games;

  return (
    <>
      <div className="grid">
        {processedGames && processedGames.map((item) => (
          <div key={item.id}>
            <GameCard
              game={item}
              isGroup={item.isGroup}
              onClick={onCardClick}
              onEditClick={onEditClick}
              isAdmin={isAdmin}
              onCategoryClick={onCategoryClick}
            />
          </div>
        ))}
      </div>
      <div ref={loaderRef} />
      {state.loading && <div aria-busy="true" className="text-center col-span-full">Loading...</div>}
      {state.error && <p className="text-center col-span-full" style={{ color: 'var(--pico-color-red-500)' }}>Error: {state.error}</p>}
      {!state.hasMore && state.games.length > 0 && <p className="text-center col-span-full">You've reached the end!</p>}
      {!state.loading && state.games.length === 0 && !state.error && <p className="text-center col-span-full">No games found for the selected filters.</p>}
    </>
  );
};

export default React.memo(GameList);
